import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import { Briefcase, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import CollaboratorPicker from '@/components/CollaboratorPicker';
import CollaboratorChips from '@/components/CollaboratorChips';

const STATUSES = ['Planning', 'Active', 'On Hold', 'Completed', 'Cancelled'];
const STATUS_COLOR = {
  Planning: 'border-sky-500/40 text-sky-300',
  Active: 'border-emerald-500/40 text-emerald-300',
  'On Hold': 'border-amber-500/40 text-amber-200',
  Completed: 'border-slate-500/40 text-slate-300',
  Cancelled: 'border-red-500/40 text-red-300',
};

const EMPTY_FORM = { name: '', client_id: '', description: '', status: 'Active', start_date: '', end_date: '', collaborators: [] };

export default function ProjectsPage() {
  const { hasPerm } = useAuth();
  // Fine-grained project permissions — each granted individually per user via Team & Users.
  const canCreate = hasPerm('project_create');
  const canEdit = hasPerm('project_edit');
  const canDelete = hasPerm('project_delete');
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);

  // Create dialog
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  const load = async () => {
    const res = await api.get('/projects');
    setProjects(res.data);
  };
  useEffect(() => {
    load();
    api.get('/clients').then((r) => setClients(r.data)).catch(() => {});
    api.get('/users').then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const create = async () => {
    const payload = { ...form };
    if (!payload.start_date) delete payload.start_date;
    if (!payload.end_date) delete payload.end_date;
    try {
      await api.post('/projects', payload);
      setOpen(false);
      setForm(EMPTY_FORM);
      toast.success('Project created');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create project');
    }
  };

  const openEdit = (p) => {
    setEditId(p.id);
    // Date inputs require YYYY-MM-DD. Slice ISO strings safely.
    const sliceDate = (d) => (typeof d === 'string' && d.length >= 10 ? d.slice(0, 10) : '');
    setEditForm({
      name: p.name || '',
      client_id: p.client_id || '',
      description: p.description || '',
      status: p.status || 'Active',
      start_date: sliceDate(p.start_date),
      end_date: sliceDate(p.end_date),
      collaborators: Array.isArray(p.collaborators) ? p.collaborators : [],
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    const payload = { ...editForm };
    // Send empty strings as null so backend treats them as cleared (or omit)
    if (!payload.start_date) delete payload.start_date;
    if (!payload.end_date) delete payload.end_date;
    try {
      await api.patch(`/projects/${editId}`, payload);
      setEditOpen(false);
      setEditId(null);
      toast.success('Project updated');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update project');
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete project "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success('Project deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete project');
    }
  };

  const updateStatus = async (p, status) => {
    try {
      await api.patch(`/projects/${p.id}`, { status });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update status');
    }
  };

  return (
    <div>
      <PageHeader
        icon={Briefcase}
        title="Projects"
        subtitle="Active engagements across all clients."
        actions={canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-project-btn"
                className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold"
              >
                <Plus className="h-4 w-4 mr-1" /> New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="text-white">New project</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Name</Label>
                  <Input
                    data-testid="project-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-[#0f2240] border-amber-500/20"
                  />
                </div>
                <div>
                  <Label>Client</Label>
                  <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                    <SelectTrigger data-testid="project-client-select" className="bg-[#0f2240] border-amber-500/20">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a1628] border-amber-500/20">
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    data-testid="project-description-input"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="bg-[#0f2240] border-amber-500/20"
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a1628] border-amber-500/20">
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Start</Label>
                    <Input
                      type="date"
                      value={form.start_date}
                      onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                      className="bg-[#0f2240] border-amber-500/20"
                    />
                  </div>
                  <div>
                    <Label>End</Label>
                    <Input
                      type="date"
                      value={form.end_date}
                      onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                      className="bg-[#0f2240] border-amber-500/20"
                    />
                  </div>
                </div>
                <div>
                  <Label>Collaborators <span className="text-[10px] text-slate-500">(team members sharing this project)</span></Label>
                  <CollaboratorPicker
                    users={users}
                    value={form.collaborators || []}
                    onChange={(next) => setForm({ ...form, collaborators: next })}
                    testIdPrefix="project-collab"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  data-testid="project-create-submit"
                  onClick={create}
                  disabled={!form.name || !form.client_id}
                  className="bg-amber-500 hover:bg-amber-400 text-[#0a1628]"
                >
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">Edit project</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input
                data-testid="project-edit-name-input"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="bg-[#0f2240] border-amber-500/20"
              />
            </div>
            <div>
              <Label>Client</Label>
              <Select value={editForm.client_id} onValueChange={(v) => setEditForm({ ...editForm, client_id: v })}>
                <SelectTrigger data-testid="project-edit-client-select" className="bg-[#0f2240] border-amber-500/20">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a1628] border-amber-500/20">
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                data-testid="project-edit-description-input"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="bg-[#0f2240] border-amber-500/20"
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger className="bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0a1628] border-amber-500/20">
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start</Label>
                <Input
                  type="date"
                  value={editForm.start_date}
                  onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                  className="bg-[#0f2240] border-amber-500/20"
                />
              </div>
              <div>
                <Label>End</Label>
                <Input
                  type="date"
                  value={editForm.end_date}
                  onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                  className="bg-[#0f2240] border-amber-500/20"
                />
              </div>
            </div>
            <div>
              <Label>Collaborators</Label>
              <CollaboratorPicker
                users={users}
                value={editForm.collaborators || []}
                onChange={(next) => setEditForm({ ...editForm, collaborators: next })}
                testIdPrefix="project-edit-collab"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="project-edit-save"
              onClick={saveEdit}
              disabled={!editForm.name || !editForm.client_id}
              className="bg-amber-500 hover:bg-amber-400 text-[#0a1628]"
            >
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {projects.length === 0 ? (
        <Card className="shine-card p-10 text-center text-slate-400 rounded-xl">No projects yet.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {projects.map((p) => (
            <Card key={p.id} data-testid={`project-card-${p.id}`} className="shine-card p-4 rounded-xl">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-bold text-white">{p.name}</h3>
                  <div className="text-xs text-amber-300 mt-0.5">{p.client_name}</div>
                </div>
                <Badge variant="outline" className={STATUS_COLOR[p.status]}>{p.status}</Badge>
              </div>
              <p className="text-sm text-slate-400 mt-2 line-clamp-3">{p.description}</p>
              {(p.start_date || p.end_date) && (
                <div className="text-xs text-slate-500 mt-2">
                  {p.start_date && new Date(p.start_date).toLocaleDateString()} → {p.end_date && new Date(p.end_date).toLocaleDateString()}
                </div>
              )}
              {Array.isArray(p.collaborators) && p.collaborators.length > 0 && (
                <div className="mt-2">
                  <CollaboratorChips ids={p.collaborators} users={users} size="xs" />
                </div>
              )}
              {(canEdit || canDelete) && (
                <div className="mt-3 flex items-center gap-2">
                  {canEdit && (
                    <Select value={p.status} onValueChange={(v) => updateStatus(p, v)}>
                      <SelectTrigger className="h-8 text-xs bg-[#0f2240] border-amber-500/20 flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a1628] border-amber-500/20">
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  {canEdit && (
                    <Button
                      data-testid={`project-edit-btn-${p.id}`}
                      size="icon"
                      variant="outline"
                      onClick={() => openEdit(p)}
                      className="h-8 w-8 border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                      title="Edit project"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      data-testid={`project-delete-btn-${p.id}`}
                      size="icon"
                      variant="outline"
                      onClick={() => remove(p)}
                      className="h-8 w-8 border-red-500/40 text-red-300 hover:bg-red-500/10"
                      title="Delete project"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
